import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Shield, ArrowLeft, Loader2, Lock, Key, Smartphone, Mail } from "lucide-react";
import axios from "axios";
import useUser from "@/hooks/useUser";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SecuritySettingsPage() {
  const navigate = useNavigate();
  const { user, isLoggedIn, isLoading } = useUser();
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, isLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchTwoFAStatus();
    } else if (!isLoading) {
      // If user is null and not loading, stop the loading state
      setLoading(false);
    }
  }, [user, isLoading]);

  const fetchTwoFAStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/2fa/status`, { withCredentials: true });
      setTwoFAEnabled(response.data.two_fa_enabled);
    } catch (error) {
      console.error("Failed to fetch 2FA status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = async (enabled) => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/2fa/enable`, {
        enabled: enabled
      }, { withCredentials: true });

      if (response.data.success) {
        setTwoFAEnabled(enabled);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update 2FA settings");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-3xl mx-auto px-4 py-8 pt-24">
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-7 h-7 text-violet-600" />
            Security Settings
          </h1>
          <p className="text-gray-500 mt-1">Manage your account security preferences</p>
        </div>

        {/* 2FA Card */}
        <Card className="p-6 mb-6" data-testid="2fa-settings-card">
          <div className="flex items-start justify-between">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Two-Factor Authentication (2FA)</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Add an extra layer of security to your account. When enabled, you'll need to enter an OTP sent to your email each time you login.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{user?.email}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {saving && <Loader2 className="w-4 h-4 animate-spin text-violet-600" />}
              <Switch
                checked={twoFAEnabled}
                onCheckedChange={handleToggle2FA}
                disabled={saving}
                data-testid="2fa-toggle"
              />
            </div>
          </div>
          
          {twoFAEnabled && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                2FA is active. You'll receive a 6-digit OTP on each login.
              </p>
            </div>
          )}
        </Card>

        {/* Password Card */}
        <Card className="p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Key className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Password</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Change your password regularly to keep your account secure.
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/forgot-password")}>
              Change Password
            </Button>
          </div>
        </Card>

        {/* Sessions Card */}
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Lock className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Active Sessions</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Manage devices where you're currently logged in.
                </p>
                {user?.last_login && (
                  <p className="text-xs text-gray-400 mt-2">
                    Last login: {new Date(user.last_login).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" className="text-red-600 hover:bg-red-50" disabled>
              Logout All Devices
            </Button>
          </div>
        </Card>

        {/* Security Tips */}
        <div className="mt-8 p-4 bg-violet-50 rounded-lg border border-violet-100">
          <h4 className="font-medium text-violet-900 mb-2">Security Tips</h4>
          <ul className="text-sm text-violet-700 space-y-1">
            <li>• Enable 2FA for enhanced protection</li>
            <li>• Use a strong, unique password</li>
            <li>• Never share your OTP with anyone</li>
            <li>• Logout from devices you don't recognize</li>
          </ul>
        </div>
      </main>

      <Footer />
    </div>
  );
}
